import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseAgreementForm, { residualErrors } from '../../../../hooks/useLegalCaseAgreementForm';
import type { AddLegalAgreementValue } from '../../../../hooks/useLegalCaseAgreements';

interface LegalCaseAgreementFormProps {
    errorFields: ErrorField[];
    isSaving: boolean;
    // Rótulo do botão primário: "Registrar acordo" na criação, "Renegociar" na superseção.
    submitLabel?: string;
    onSubmit: (value: AddLegalAgreementValue) => Promise<boolean>;
    onCancel: () => void;
}

// Só JSX + bindings: estado e regras do formulário vivem em useLegalCaseAgreementForm. É o
// mesmo form da criação e da renegociação (superseção) — os campos do acordo são idênticos,
// muda só o rótulo do botão e o que o pai faz com o valor.
const LegalCaseAgreementForm = ({
    errorFields,
    isSaving,
    submitLabel = 'Registrar acordo',
    onSubmit,
    onCancel,
}: LegalCaseAgreementFormProps) => {
    const form = useLegalCaseAgreementForm({ onSubmit });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor acordado (opcional)"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.agreedAmount}
                    onChange={form.setAgreedAmount}
                    errorMessage={getErrorMessage(errorFields, 'agreedAmount')}
                />
                <DateField
                    label="Data de assinatura (opcional)"
                    value={form.signedAt}
                    onChange={form.setSignedAt}
                    errorMessage={getErrorMessage(errorFields, 'signedAt')}
                />
            </div>

            <TextEdit
                label="Observações (opcional)"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : submitLabel}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseAgreementForm;
